import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { IconWorld } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useFetcherPublic } from '@app/hooks/fetcher';

const earthIconUrl = '/assets/newGNB/icon-earth.svg';

const SelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
  position: relative;
`;

const LabelText = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  letter-spacing: -0.28px;
  margin: 0;
`;

const DropdownBridge = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 200px;
  height: 8px;
  z-index: 1001;
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #0d1117;
  border-radius: 12px;
  width: 200px;
  display: ${(props) => (props.$isOpen ? 'flex' : 'none')};
  flex-direction: column;
  padding: 20px 12px;
  z-index: 1001;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: ${(props) => (props.$isOpen ? 'auto' : 'none')};
`;

const MenuHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px;
  margin-bottom: 0;
`;

const MenuHeaderText = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  letter-spacing: -0.28px;
  margin: 0;
`;

const MenuItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const MenuItemText = styled.p<{ $isSelected: boolean }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => (props.$isSelected ? '#ff8a00' : '#b3b5bd')};
  letter-spacing: -0.28px;
  margin: 0;
`;

const LanguageSelector: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data } = useFetcherPublic('/system/language', false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // 언어 목록 필터링 (English, Korean, Japan, China)
  const languageMap: { [key: string]: string } = {
    en: 'English',
    ko: 'Korean',
  };

  const availableLanguages = [
    { code: 'en', name: 'English' },
    { code: 'ko', name: 'Korean' },
  ];

  const currentLanguage = i18n.language.toLowerCase().substring(0, 2);

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <SelectorContainer
      ref={dropdownRef}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <IconWorld size={24} color="#ffffff" />
      <LabelText>{t("Language")}</LabelText>
      {isOpen && <DropdownBridge />}
      <DropdownMenu $isOpen={isOpen}>
        <MenuHeader>
          <img
            src={earthIconUrl}
            alt="earth"
            style={{ width: '24px', height: '24px' }}
          />
          <MenuHeaderText>{t("Language")}</MenuHeaderText>
        </MenuHeader>
        {availableLanguages.map((lang) => (
          <MenuItem
            key={lang.code}
            $isSelected={currentLanguage === lang.code}
            onClick={() => handleLanguageChange(lang.code)}
          >
            <MenuItemText $isSelected={currentLanguage === lang.code}>
              {lang.name}
            </MenuItemText>
          </MenuItem>
        ))}
      </DropdownMenu>
    </SelectorContainer>
  );
};

export default LanguageSelector;
